import type { Phone, Address } from '../../api/models/bff/Person';
import type { DateTimeInfo, DateTimePeriod } from '../../api/models/bff/Common';
import type { DetailedPriceChargeRate, CompletePrice, AdditionalCharge } from '../../api/models/bff/Price';
import type {
  Merchant,
  Payer,
  ReceiptItem,
  ReceiptItemProduct,
  ReceiptResponse,
} from '../../api/models/bff/Receipt';
import { generateDateWithDayOffset, generateId } from '../utils/generators';

export function createMockReceiptDateTime(utc?: string): DateTimeInfo {
  return {
    utc: utc ?? new Date().toISOString(),
    zoneId: 'UTC',
  };
}

export function createMockReceiptPeriod(overrides?: Partial<DateTimePeriod>): DateTimePeriod {
  return {
    start: createMockReceiptDateTime(generateDateWithDayOffset(-30)),
    end: createMockReceiptDateTime(generateDateWithDayOffset(0)),
    ...overrides,
  };
}

export function createMockReceiptGrossNetPrice(overrides?: Partial<CompletePrice>): CompletePrice {
  return {
    net: { amount: '48.76', currency: 'EUR' },
    gross: { amount: '59.00', currency: 'EUR' },
    ...overrides,
  };
}

export function createMockReceiptCharge(overrides?: Partial<AdditionalCharge>): AdditionalCharge {
  return {
    type: 'VAT',
    code: 'VAT_EUR',
    rate: { value: '21', type: 'PERCENTAGE' } as DetailedPriceChargeRate,
    amount: { amount: '10.24', currency: 'EUR' },
    ...overrides,
  };
}

export function createMockReceiptItem(overrides?: Partial<ReceiptItem>): ReceiptItem {
  return {
    code: 'VEL-BASIC-SOLO-RT1-MONTHLY-PLAN',
    product: {
      code: 'BASIC-SOLO-RT1-MONTHLY',
      type: 'BASE',
      family: 'BASIC',
    } as ReceiptItemProduct,
    period: createMockReceiptPeriod(),
    price: createMockReceiptGrossNetPrice(),
    ...overrides,
  };
}

export function createMockReceiptAddonItem(overrides?: Partial<ReceiptItem>): ReceiptItem {
  return {
    code: 'VEL-UNDERSEATBAG-INCLUDED-MONTHLY-PLAN',
    product: {
      code: 'VEL-UNDERSEATBAG-INCLUDED-MONTHLY',
      type: 'ADD_ON',
      ancillary: 'UNDERSEATBAG',
    } as ReceiptItemProduct,
    period: null,
    price: createMockReceiptGrossNetPrice({
      net: { amount: '0.00', currency: 'EUR' },
      gross: { amount: '0.00', currency: 'EUR' },
    }),
    ...overrides,
  };
}

export function createMockReceiptExternalChargeItem(overrides?: Partial<ReceiptItem>): ReceiptItem {
  return {
    code: 'External charge',
    product: null,
    period: null,
    price: createMockReceiptGrossNetPrice({
      net: { amount: '500.00', currency: 'EUR' },
      gross: { amount: '500.00', currency: 'EUR' },
    }),
    ...overrides,
  };
}

export function createMockReceiptPayer(overrides?: Partial<Payer>): Payer {
  return {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phones: [{ prefix: '+1', number: '5551234567', type: 'MOBILE' }] as Phone[],
    addresses: [
      {
        type: 'HOME',
        street: '456 Oak Avenue',
        city: 'Springfield',
        state: 'IL',
        postalCode: '62704',
        country: 'US',
      },
    ] as Address[],
    ...overrides,
  };
}

export function createMockReceiptMerchant(overrides?: Partial<Merchant>): Merchant {
  return {
    name: 'Velo Air',
    email: 'support@veloair.com',
    phones: [{ prefix: '+1', number: '234567890', type: 'WORK' }] as Phone[],
    addresses: [
      {
        type: 'BUSINESS',
        street: '123 Main St',
        apartment: 'Suite 100',
        city: 'Anytown',
        state: 'CA',
        postalCode: '12345',
        country: 'US',
      },
    ] as Address[],
    ...overrides,
  };
}

export function createMockReceipt(overrides?: Partial<ReceiptResponse>): ReceiptResponse {
  return {
    id: generateId('receipt'),
    number: '657261',
    createdAt: createMockReceiptDateTime(generateDateWithDayOffset(-2)),
    status: 'PAID',
    payer: createMockReceiptPayer(),
    merchant: createMockReceiptMerchant(),
    period: createMockReceiptPeriod(),
    items: [createMockReceiptItem(), createMockReceiptAddonItem()],
    additionalCharges: [createMockReceiptCharge()],
    subtotal: createMockReceiptGrossNetPrice(),
    total: createMockReceiptGrossNetPrice(),
    paid: { amount: '59.00', currency: 'EUR' },
    refunded: { amount: '0.00', currency: 'EUR' },
    balance: { amount: '0.00', currency: 'EUR' },
    ...overrides,
  };
}

export function createMockReceiptResponse(overrides?: Partial<ReceiptResponse>): ReceiptResponse {
  return createMockReceipt(overrides);
}
